import { WidgetType } from "@src/types/dashboards";
import {
  MULTIPLE_TEXT_VALUES_MIN_ENTRIES,
  multipleTextValuesHeight,
} from "./multiple-text-values/multiple-text-values-height";

export interface WidgetSize {
  w: number; // default width; for defined types below it also serves as minW
  h: number; // default height; for defined types below it also serves as minH
  minW: number;
  minH: number;
  maxW: number;
  maxH?: number;
}

// FE-decided sizes, sent in the add-widget POST. `w`/`h` double as the minimum.
// ponytail: hardcoded for now; later possibly derived from the template.
// maxW/maxH are placeholders for the gauge — tune when the design lands.
// ponytail: gauge constraint set shared with the line chart per user (same
// min/max as the gauge); tune when the design lands.
const GAUGE_LIKE_SIZE: WidgetSize = {
  w: 5,
  h: 5,
  minW: 5,
  minH: 5,
  maxW: 12,
  maxH: 9,
};

// The chart needs vertical room (title + plot + legend), so it is taller
// than the gauge's value display. User: gauge-size was "too shrinked".
const TREND_LINE_SIZE: WidgetSize = {
  w: 6,
  h: 7,
  minW: 6,
  minH: 7,
  maxW: 12,
  maxH: 9,
};

// ponytail: multi-line follows the gauge-like defaults per spec §2 — slightly
// shorter than trend-line because the legend wraps onto multiple lines for
// 2–5 series anyway, and the chart is meant to fit alongside other widgets
// without dominating the canvas.
const MULTI_LINE_SIZE: WidgetSize = {
  w: 5,
  h: 5,
  minW: 5,
  minH: 5,
  maxW: 12,
  maxH: 9,
};

// Shot-data line chart: same footprint as the multi-line chart — a line
// chart with a legend for up to 5 series needs the same vertical room.
const SHOT_DATA_LINE_CHART_SIZE: WidgetSize = {
  w: 5,
  h: 5,
  minW: 5,
  minH: 5,
  maxW: 12,
  maxH: 9,
};

// Static tools (header, separator) are visual spacers, not data widgets.
// They don't have a body to fill, so their height is tight — height is
// locked per-type, width stays flexible so they can span the canvas.
const STATIC_SEPARATOR_SIZE: WidgetSize = {
  w: 6,
  h: 1,
  minW: 1,
  minH: 1,
  maxW: 24,
  maxH: 1,
};

const STATIC_HEADER_SIZE: WidgetSize = {
  w: 6,
  h: 1,
  minW: 1,
  minH: 1,
  maxW: 24,
  maxH: 2,
};

const STATIC_TEXT_SIZE: WidgetSize = {
  w: 4,
  h: 1,
  minW: 1,
  minH: 1,
  maxW: 24,
  maxH: 12,
};

const MACHINE_OVERVIEW_SIZE: WidgetSize = {
  w: 6,
  h: 8,
  minW: 6,
  minH: 8,
  maxW: 12,
};

// ponytail: single-text-value fits in a tight footprint (label + value +
// unit + timestamp stack), so we let the user resize it smaller than the
// gauge while still keeping the same ceiling. Floor 2x2: at that size the
// label+value+unit can sit at the font-size floor without crowding.
const SINGLE_TEXT_VALUE_SIZE: WidgetSize = {
  w: 4,
  h: 4,
  minW: 2,
  minH: 2,
  maxW: 12,
  maxH: 9,
};

// Multiple Values Text: width behaves like single-text-value (same default
// and floor/ceiling). The height may only grow: `getWidgetSize` sets the
// floor from the entry count, so content can never be cut off. This entry
// is the seed used before the first save (picker tile, library row).
const MULTIPLE_TEXT_VALUES_SIZE: WidgetSize = {
  w: 4,
  h: multipleTextValuesHeight(MULTIPLE_TEXT_VALUES_MIN_ENTRIES),
  minW: 4,
  minH: multipleTextValuesHeight(MULTIPLE_TEXT_VALUES_MIN_ENTRIES),
  maxW: 24,
};

const WIDGET_SIZES: Partial<Record<WidgetType, WidgetSize>> = {
  "counter-number": GAUGE_LIKE_SIZE,
  "single-text-value": SINGLE_TEXT_VALUE_SIZE,
  "multiple-text-values": MULTIPLE_TEXT_VALUES_SIZE,
  "trend-line": TREND_LINE_SIZE,
  "multi-line": MULTI_LINE_SIZE,
  "shot-data-line-chart": SHOT_DATA_LINE_CHART_SIZE,
  "machine-overview": MACHINE_OVERVIEW_SIZE,
  "static-header": STATIC_HEADER_SIZE,
  "static-separator": STATIC_SEPARATOR_SIZE,
  "static-text": STATIC_TEXT_SIZE,
  "static-link": STATIC_TEXT_SIZE,
};

const DEFAULT_WIDGET_SIZE: WidgetSize = {
  w: 4,
  h: 2,
  minW: 2,
  minH: 2,
  maxW: 24,
  maxH: 12,
};

/** Resolve a widget type's grid size.
 *
 *  `entryCount` is the number of entries the widget holds. Callers may pass
 *  it for every widget type — only `multiple-text-values` reads it, to
 *  floor its height at what its content needs. */
export const getWidgetSize = (
  type: WidgetType,
  entryCount?: number,
): WidgetSize => {
  const size = WIDGET_SIZES[type] ?? DEFAULT_WIDGET_SIZE;
  if (type !== "multiple-text-values" || entryCount === undefined) return size;
  return { ...size, minH: multipleTextValuesHeight(entryCount) };
};
